import { Fragment, useMemo, useState } from 'react'

import {
	Article as ArticleIcon,
	DeleteOutline as DeleteOutlineIcon,
	Download as DownloadIcon,
	EditOutlined as EditOutlinedIcon,
	Event as EventIcon,
	InsertPhoto as InsertPhotoIcon,
	Lock as LockIcon,
	LocationOn as LocationOnIcon,
	OndemandVideo as OndemandVideoIcon,
	OpenInNew as OpenInNewIcon,
	Person as PersonIcon,
	Poll as PollIcon,
	VolumeUp as VolumeUpIcon,
} from '@mui/icons-material'

import {
	CircularProgress,
	Tooltip,
} from '@mui/material'

import DefaultModal from '../modal/DefaultModal/DefaultModal'

import {
	formatDateLabel,
	formatKwikMessageContent,
	getContactMessageDetails,
	getEventMessageDetails,
	getLocationMessageDetails,
	getPollMessageDetails,
} from '../../services/scout'
import { highlightQuery } from '../search/SearchHighlight'

import { isMessageTypeAllowedForEnterprise } from '../../services/utils'

const MEDIA_MESSAGE_TYPES = new Set([
	'imageMessage',
	'videoMessage',
	'documentMessage',
	'documentWithCaptionMessage',
	'audioMessage',
	'stickerMessage',
])

export const MESSAGE_TYPE_META = {
	imageMessage: { label: 'Imagem', icon: <InsertPhotoIcon />, mimeType: 'image/jpeg' },
	videoMessage: { label: 'Vídeo', icon: <OndemandVideoIcon />, mimeType: 'video/mp4' },
	documentMessage: { label: 'Documento', icon: <ArticleIcon />, mimeType: 'application/octet-stream' },
	documentWithCaptionMessage: { label: 'Documento', icon: <ArticleIcon />, mimeType: 'application/octet-stream' },
	audioMessage: { label: 'Áudio', icon: <VolumeUpIcon />, mimeType: 'audio/ogg' },
	stickerMessage: { label: 'Figurinha', icon: <InsertPhotoIcon />, mimeType: 'image/webp' },
	eventMessage: { label: 'Evento', icon: <EventIcon />, mimeType: null },
	locationMessage: { label: 'Localização', icon: <LocationOnIcon />, mimeType: null },
	liveLocationMessage: { label: 'Localização', icon: <LocationOnIcon />, mimeType: null },
	contactMessage: { label: 'Contato', icon: <PersonIcon />, mimeType: null },
	pollCreationMessageV3: { label: 'Enquete', icon: <PollIcon />, mimeType: null },
}

export const getMessagePayload = (message) => message?.message ?? message

export const getMessageIdentifier = (message, fallback = '') => {
	return String(message?.id || message?.chatMessageId || message?.messageId || fallback)
}

export const getHistoryEntryKey = (message) => {
	const protocolId = message?.protocolId || message?.protocol_id || ''
	const timestamp = message?.timestamp || ''
	const messageType = message?.messageType || ''
	const simplifiedType = message?.simplified_type || ''
	const textContent = typeof message?.message === 'string' ? message.message.trim() : ''
	const caption = typeof message?.caption === 'string' ? message.caption.trim() : ''
	const title = typeof message?.title === 'string' ? message.title.trim() : ''

	return [message?.type || '', protocolId, timestamp, messageType, simplifiedType, textContent, caption, title].join('|')
}

export const getMessageKey = (message, index = 0) => {
	const messageKey = getMessageIdentifier(message, String(index))

	if (message?.type) {
		return `${messageKey}|history|${getHistoryEntryKey(message)}`
	}

	return messageKey
}

const getMessageValue = (message, key) => {
	const payload = getMessagePayload(message)
	return payload?.[key]
}

export const hasMediaPayload = (message) => {
	const payload = getMessagePayload(message)
	const base64 = typeof payload?.base64 === 'string' ? payload.base64.trim() : ''
	const mediaUrl = typeof payload?.mediaUrl === 'string' ? payload.mediaUrl.trim() : ''

	return Boolean(base64 || mediaUrl)
}

export const isMediaMessage = (message) => MEDIA_MESSAGE_TYPES.has(getMessageValue(message, 'messageType'))

export const getMediaMimeType = (message) => {
	return getMessageValue(message, 'mimetype') || MESSAGE_TYPE_META[getMessageValue(message, 'messageType')]?.mimeType || 'application/octet-stream'
}

export const getMediaTitle = (message) => {
	const documentMessage = getMessageValue(message, 'documentMessage')

	return (
		getMessageValue(message, 'title') ||
		getMessageValue(message, 'fileName') ||
		documentMessage?.fileName ||
		MESSAGE_TYPE_META[getMessageValue(message, 'messageType')]?.label ||
		'Arquivo'
	)
}

export const getMediaCaption = (message) => {
	const payload = getMessagePayload(message)
	const mediaMessageContent = payload?.message

	if (typeof mediaMessageContent === 'string' && mediaMessageContent.trim()) {
		return mediaMessageContent.trim()
	}

	if (typeof payload?.caption === 'string' && payload.caption.trim()) {
		return payload.caption.trim()
	}

	return ''
}

export const getMediaSource = (message) => {
	const payload = getMessagePayload(message)
	const base64 = typeof payload?.base64 === 'string' ? payload.base64.trim() : ''

	if (base64) {
		return `data:${getMediaMimeType(message)};base64,${base64}`
	}

	return typeof payload?.mediaUrl === 'string' ? payload.mediaUrl.trim() : ''
}

export const formatMediaSize = (value) => {
	const numericValue = Number(value)

	if (!numericValue || Number.isNaN(numericValue)) {
		return ''
	}

	if (numericValue < 1024 * 1024) {
		return `${Math.round(numericValue / 1024)} KB`
	}

	return `${(numericValue / (1024 * 1024)).toFixed(2)} MB`
}

export const createObjectUrlFromBase64 = (base64, mimeType) => {
	const byteCharacters = window.atob(base64)
	const byteNumbers = new Array(byteCharacters.length)

	for (let index = 0; index < byteCharacters.length; index += 1) {
		byteNumbers[index] = byteCharacters.charCodeAt(index)
	}

	const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType })
	return URL.createObjectURL(blob)
}

export const openMediaInNewTab = (message) => {
	const payload = getMessagePayload(message)
	const base64 = typeof payload?.base64 === 'string' ? payload.base64.trim() : ''
	const mediaSource = base64
		? createObjectUrlFromBase64(base64, getMediaMimeType(message))
		: getMediaSource(message)

	if (!mediaSource) {
		return
	}

	const link = document.createElement('a')
	link.href = mediaSource
	link.target = '_blank'
	link.rel = 'noopener noreferrer'
	document.body.appendChild(link)
	link.click()
	link.remove()

	if (base64) {
		window.setTimeout(() => URL.revokeObjectURL(mediaSource), 60000)
	}
}

export const downloadMediaFile = (message) => {
	const mediaSource = getMediaSource(message)
	if (!mediaSource) {
		return
	}

	const link = document.createElement('a')
	link.href = mediaSource
	link.download = getMediaTitle(message)
	link.target = '_blank'
	link.rel = 'noopener noreferrer'
	document.body.appendChild(link)
	link.click()
	link.remove()
}

const MediaPlaceholder = ({ message, loading, onLoad }) => {
	const mediaMeta = MESSAGE_TYPE_META[message?.messageType] || MESSAGE_TYPE_META.documentMessage
	const thumbnailSrc = message?.thumbnail ? `data:image/jpeg;base64,${message.thumbnail}` : ''
	const fileSize = formatMediaSize(message?.size)

	return (
		<button
			type="button"
			className="scout-media-placeholder"
			onClick={onLoad}
			disabled={loading}
		>
			{thumbnailSrc ? (
				<img className="scout-media-placeholder__thumbnail" src={thumbnailSrc} alt={mediaMeta.label} />
			) : (
				<span className="scout-media-placeholder__icon">{mediaMeta.icon}</span>
			)}
			<span className="scout-media-placeholder__content">
				<strong>Carregar {mediaMeta.label.toLowerCase()}</strong>
				<span>{getMediaTitle(message)}</span>
				{fileSize ? <span>{fileSize}</span> : null}
			</span>
			{loading ? <CircularProgress size={18} /> : null}
		</button>
	)
}

const LockedMediaPlaceholder = ({ message }) => {
	const mediaMeta = MESSAGE_TYPE_META[message?.messageType] || MESSAGE_TYPE_META.documentMessage

	return (
		<div className="scout-media-locked">
			<span className="scout-media-locked__icon">
				<LockIcon fontSize="small" />
			</span>
			<span className="scout-media-locked__content">
				<strong>{mediaMeta.label}</strong>
				<span>Este tipo de mídia está bloqueado no seu plano</span>
			</span>
		</div>
	)
}

const EventMessageCard = ({ message }) => {
	const details = getEventMessageDetails(message)

	if (!details) {
		return <p>{formatKwikMessageContent(message)}</p>
	}

	return (
		<div className="scout-document-card">
			<div className="scout-document-card__header">
				<span className="scout-document-card__icon"><EventIcon /></span>
				<div className="scout-document-card__meta" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
					<strong>{details.title}</strong>
					{details.description ? <span>{details.description}</span> : null}
					{details.startLabel ? <span>Início: {details.startLabel}</span> : null}
					{details.endLabel ? <span>Fim: {details.endLabel}</span> : null}
					{details.locationLabel ? <span>Local: {details.locationLabel}</span> : null}
					{details.joinLink ? (
						<button type="button" className="scout-document-card__button" onClick={() => window.open(details.joinLink, '_blank', 'noopener,noreferrer')}>
							<DownloadIcon fontSize="inherit" />
							<span>Entrar na ligação</span>
						</button>
					) : null}
				</div>
			</div>
		</div>
	)
}

const buildPollVoterIdentifierVariants = (value) => {
	const normalized = String(value || '').trim()
	if (!normalized) {
		return []
	}

	const variants = new Set([normalized])
	const base = normalized.split('@')[0]
	if (base) {
		variants.add(base)
		variants.add(`${base}@lid`)
		variants.add(`${base}@s.whatsapp.net`)
	}

	return Array.from(variants)
}

const buildPollVoterNameMap = (messages = []) => {
	const voterNameMap = new Map()

	if (!Array.isArray(messages)) {
		return voterNameMap
	}

	messages.forEach((entry) => {
		const pushName = String(entry?.pushName || entry?.push_name || '').trim()
		if (!pushName) {
			return
		}

		const identifiers = [
			entry?.participant,
			entry?.key?.participant,
			entry?.remoteJid,
			entry?.remote_jid,
			entry?.key?.remoteJid,
		]

		identifiers.forEach((identifier) => {
			buildPollVoterIdentifierVariants(identifier).forEach((variant) => {
				if (!voterNameMap.has(variant)) {
					voterNameMap.set(variant, pushName)
				}
			})
		})
	})

	return voterNameMap
}

const getPollVoterIdentifier = (voter) => {
	if (typeof voter === 'string') {
		return voter
	}

	if (voter && typeof voter === 'object') {
		return voter.id || voter.jid || voter.voter || voter.remoteJid || ''
	}

	return ''
}

const getPollVoterStoredName = (voter) => {
	if (!voter || typeof voter !== 'object') {
		return ''
	}

	return String(voter.name || voter.pushName || voter.displayName || '').trim()
}

const resolvePollVoterDisplayName = (voter, voterNameMap) => {
	const storedName = getPollVoterStoredName(voter)
	if (storedName) {
		return storedName
	}

	const voterIdentifier = getPollVoterIdentifier(voter)
	const variants = buildPollVoterIdentifierVariants(voterIdentifier)
	for (const variant of variants) {
		if (voterNameMap.has(variant)) {
			return voterNameMap.get(variant)
		}
	}

	return voterIdentifier || 'Participante'
}

const PollMessageCard = ({ message, messages = [] }) => {
	const details = getPollMessageDetails(message)
	const [votesOpen, setVotesOpen] = useState(false)
	const voterNameMap = useMemo(() => buildPollVoterNameMap(messages), [messages])

	if (!details) {
		return <p>{formatKwikMessageContent(message)}</p>
	}

	return (
		<>
			<div className="scout-document-card scout-poll-card">
				<div className="scout-document-card__header scout-poll-card__header">
					<span className="scout-document-card__icon scout-poll-card__icon"><PollIcon /></span>
					<div className="scout-document-card__meta scout-poll-card__meta">
						<strong>{details.title}</strong>
						<div className="scout-poll-card__options">
							{details.options.map((option) => {
								const percentage = details.totalVotes > 0
									? Math.round((Number(option.voteCount || 0) / details.totalVotes) * 100)
									: 0

								return (
									<div key={option.id} className="scout-poll-card__option">
										<div className="scout-poll-card__option-topline">
											<strong>{option.name}</strong>
											<span>{option.voteCount}</span>
										</div>
										<div className="scout-poll-card__bar" aria-hidden="true">
											<span className="scout-poll-card__bar-fill" style={{ width: `${percentage}%` }} />
										</div>
									</div>
								)
							})}
						</div>
						<button
							type="button"
							className="scout-poll-card__toggle"
							onClick={() => setVotesOpen(true)}
						>
							Mostrar votos
						</button>
					</div>
				</div>
			</div>

			{votesOpen ? (
				<DefaultModal
					open={votesOpen}
					handleClose={() => setVotesOpen(false)}
					title="Votos da enquete"
					subtitle={details.title}
					maxWidth="sm"
				>
					<div className="scout-poll-votes-modal">
						{details.options.map((option) => (
							<div key={`vote-${option.id}`} className="scout-poll-votes-modal__section">
								<div className="scout-poll-votes-modal__section-header">
									<strong>{option.name}</strong>
									<span>{option.voteCount} voto{Number(option.voteCount || 0) === 1 ? '' : 's'}</span>
								</div>
								{option.voters?.length ? (
									<ul className="scout-poll-votes-modal__list">
										{option.voters.map((voter, index) => (
											<li key={`${option.id}-voter-${index}`}>{resolvePollVoterDisplayName(voter, voterNameMap)}</li>
										))}
									</ul>
								) : (
									<p className="scout-poll-votes-modal__empty">Nenhum voto nesta opção.</p>
								)}
							</div>
						))}
					</div>
				</DefaultModal>
			) : null}
		</>
	)
}

const ContactMessageCard = ({ message }) => {
	const details = getContactMessageDetails(message)

	if (!details) {
		return <p>{formatKwikMessageContent(message)}</p>
	}

	return (
		<div className="scout-document-card">
			<div className="scout-document-card__header">
				<span className="scout-document-card__icon"><PersonIcon /></span>
				<div className="scout-document-card__meta" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
					<strong>{details.title}</strong>
					{details.subtitle ? <span>{details.subtitle}</span> : null}
					{details.phones?.length ? details.phones.map((phone, index) => <span key={`${phone}-${index}`}>Telefone: {phone}</span>) : null}
					{details.primaryEmail ? <span>E-mail: {details.primaryEmail}</span> : null}
					{details.address ? <span>Endereço: {details.address}</span> : null}
					{details.businessDescription ? <span>{details.businessDescription}</span> : null}
				</div>
			</div>
		</div>
	)
}

const LocationMessageCard = ({ message }) => {
	const details = getLocationMessageDetails(message)

	if (!details) {
		return <p>{formatKwikMessageContent(message)}</p>
	}

	const thumbnailSrc = details.thumbnail ? `data:image/jpeg;base64,${details.thumbnail}` : ''

	return (
		<div className="scout-document-card">
			<div className="scout-document-card__header scout-location-card__header">
				<div className="scout-location-card__media">
					{thumbnailSrc ? (
						<img
							className="scout-location-card__thumbnail"
							src={thumbnailSrc}
							alt={details.title}
						/>
					) : (
						<span className="scout-document-card__icon">
							<LocationOnIcon />
						</span>
					)}
				</div>

				<div
					className="scout-document-card__meta scout-location-card__meta"
					style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
				>
					<strong>{details.title}</strong>
					{details.address ? <span>Endereço: {details.address}</span> : null}
					{details.coordinatesLabel ? <span>Coordenadas: {details.coordinatesLabel}</span> : null}
					{details.mapUrl ? (
						<button
							type="button"
							className="scout-document-card__button scout-location-card__button"
							onClick={() => window.open(details.mapUrl, '_blank', 'noopener,noreferrer')}
						>
							<OpenInNewIcon fontSize="inherit" />
							<span>Abrir no mapa</span>
						</button>
					) : null}
				</div>
			</div>
		</div>
	)
}

const DocumentMessageCard = ({ message }) => {
	const caption = getMediaCaption(message)

	return (
		<div className="scout-document-card">
			<div className="scout-document-card__header">
				<span className="scout-document-card__icon"><ArticleIcon /></span>
				<div className="scout-document-card__meta">
					<strong>{getMediaTitle(message)}</strong>
					{getMediaMimeType(message) ? <span>{getMediaMimeType(message)}</span> : null}
				</div>
			</div>
			<div className="scout-document-card__actions">
				<button type="button" className="scout-document-card__button" onClick={() => openMediaInNewTab(message)}>
					<OpenInNewIcon fontSize="inherit" />
					<span>Abrir</span>
				</button>
				<button type="button" className="scout-document-card__button" onClick={() => downloadMediaFile(message)}>
					<DownloadIcon fontSize="inherit" />
					<span>Baixar</span>
				</button>
			</div>
			{caption ? <p>{caption}</p> : null}
		</div>
	)
}

export const isCardMessage = (message) => {
	const messageType = message?.messageType
	const simplifiedType = message?.simplified_type

	return [
		'documentMessage',
		'documentWithCaptionMessage',
		'contactMessage',
		'locationMessage',
		'eventMessage',
		'pollCreationMessageV3',
	].includes(messageType) || ['contact', 'location', 'event', 'poll_creaction_message_v3'].includes(simplifiedType)
}

const URL_SEGMENT_REGEX = /((?:https?:\/\/|www\.)[^\s<]+)/gi

export const LinkifiedText = ({ text }) => {
	const normalizedText = typeof text === 'string' ? text : String(text ?? '')
	const lines = normalizedText.split(/\r?\n/)

	return (
		<>
			{lines.map((line, lineIndex) => {
				const segments = line.split(URL_SEGMENT_REGEX)

				return (
					<Fragment key={`line-${lineIndex}`}>
						{segments.map((segment, segmentIndex) => {
							if (!segment) {
								return null
							}

							const isUrl = /^(?:https?:\/\/|www\.)/i.test(segment)
							if (!isUrl) {
								return <Fragment key={`segment-${lineIndex}-${segmentIndex}`}>{segment}</Fragment>
							}

							const href = /^https?:\/\//i.test(segment) ? segment : `https://${segment}`

							return (
								<a
									key={`segment-${lineIndex}-${segmentIndex}`}
									href={href}
									target="_blank"
									rel="noopener noreferrer"
									className="scout-message__link"
								>
									{segment}
								</a>
							)
						})}
						{lineIndex < lines.length - 1 ? <br /> : null}
					</Fragment>
				)
			})}
		</>
	)
}

export const isRenderableMessage = (message) => {
	return !message?.type && message?.messageType !== 'reactionMessage'
}

export const getEditHistory = (message, messages = []) => {
	if (!message?.id || !Array.isArray(messages)) {
		return []
	}

	const messageId = getMessageIdentifier(message)
	const seenHistoryEntries = new Set()

	return messages
		.filter((entry) => {
			const protocolId = entry?.protocolId || entry?.protocol_id
			return Boolean(entry?.type && protocolId && String(protocolId) === messageId)
		})
		.filter((entry) => {
			const historyKey = getHistoryEntryKey(entry)
			if (seenHistoryEntries.has(historyKey)) {
				return false
			}

			seenHistoryEntries.add(historyKey)
			return true
		})
		.sort((first, second) => Number(first?.timestamp || 0) - Number(second?.timestamp || 0))
}

const getLatestMessageVersion = (message, messages = []) => {
	const history = getEditHistory(message, messages)
	return history.length ? history[history.length - 1] : message
}

const getReactionTargetId = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return ''
	}

	return String(reactionPayload.reactedMessageId || reactionPayload.messageId || '')
}

const getReactionEmoji = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return ''
	}

	if (typeof reactionPayload.reaction === 'string') {
		return reactionPayload.reaction
	}

	return ''
}

const getReactionActorKey = (message) => {
	const reactionPayload = message?.message
	if (!reactionPayload || typeof reactionPayload !== 'object') {
		return getMessageIdentifier(message)
	}

	return String(reactionPayload.contact || reactionPayload.fromMe || getMessageIdentifier(message))
}

export const buildMessageReactionLookup = (messages = []) => {
	const reactionsByMessageId = new Map()

	messages
		.filter((entry) => entry?.messageType === 'reactionMessage')
		.sort((first, second) => Number(first?.timestamp || 0) - Number(second?.timestamp || 0))
		.forEach((reactionMessage) => {
			const targetId = getReactionTargetId(reactionMessage)
			if (!targetId) {
				return
			}

			const actorKey = getReactionActorKey(reactionMessage)
			const emoji = getReactionEmoji(reactionMessage)
			const currentReactions = reactionsByMessageId.get(targetId) || new Map()

			if (emoji) {
				currentReactions.set(actorKey, {
					id: `${targetId}-${actorKey}`,
					emoji,
					fromMe: Boolean(reactionMessage?.message?.fromMe),
					timestamp: Number(reactionMessage?.timestamp || 0),
				})
			} else {
				currentReactions.delete(actorKey)
			}

			if (currentReactions.size) {
				reactionsByMessageId.set(targetId, currentReactions)
			} else {
				reactionsByMessageId.delete(targetId)
			}
		})

	return reactionsByMessageId
}

export const getMessageReactions = (message, reactionLookup) => {
	const messageId = getMessageIdentifier(message)
	if (!messageId || !(reactionLookup instanceof Map)) {
		return []
	}

	const reactions = reactionLookup.get(messageId)
	if (!(reactions instanceof Map) || !reactions.size) {
		return []
	}

	return Array.from(reactions.values()).sort((first, second) => Number(first.timestamp || 0) - Number(second.timestamp || 0))
}

export const shouldShowDateSeparator = (message, previousMessage) => {
	if (!message?.timestamp) {
		return false
	}

	if (!previousMessage?.timestamp) {
		return true
	}

	return formatDateLabel(message.timestamp * 1000) !== formatDateLabel(previousMessage.timestamp * 1000)
}

const ConversationMessageBody = ({ message, messages = [], onLoadMedia, mediaLoading, searchValue, enterprise, onOpenEditHistory }) => {
	const history = getEditHistory(message, messages)
	const latestMessage = getLatestMessageVersion(message, messages)
	const hasDeletedHistory = history.some((entry) => entry?.type === 'DELETED')
	const hasEditHistory = history.some((entry) => entry?.type === 'EDITED')
	const isDeletedMessage = latestMessage?.type === 'DELETED'
	const showHistoryAction = hasEditHistory || hasDeletedHistory
	const historyActionTooltip = hasDeletedHistory ? 'Ver mensagem original apagada' : 'Ver histórico de edição'
	const HistoryActionIcon = hasDeletedHistory ? DeleteOutlineIcon : EditOutlinedIcon
	const deletedMessageLabel = <p className="scout-message__deleted-label">Mensagem apagada</p>

	const renderHistoryActionButton = () => {
		if (!showHistoryAction) {
			return null
		}

		return (
			<Tooltip title={historyActionTooltip} arrow>
				<button
					type="button"
					className="scout-message__edited-badge"
					onClick={() => onOpenEditHistory?.(message)}
					aria-label={historyActionTooltip}
				>
					<HistoryActionIcon fontSize="small" />
				</button>
			</Tooltip>
		)
	}

	if (isMediaMessage(latestMessage)
		|| latestMessage?.messageType === 'eventMessage'
		|| latestMessage?.messageType === 'locationMessage'
		|| latestMessage?.messageType === 'liveLocationMessage'
		|| latestMessage?.messageType === 'contactMessage'
		|| latestMessage?.messageType === 'pollCreationMessageV3') {
		const allowed = isMessageTypeAllowedForEnterprise(latestMessage?.messageType, enterprise, Boolean(latestMessage?.gifPlayback))
		if (!allowed) {
			return <LockedMediaPlaceholder message={latestMessage} />
		}
	}

	if (isMediaMessage(message) && !hasMediaPayload(message)) {
		return <MediaPlaceholder message={message} loading={mediaLoading} onLoad={() => onLoadMedia(message)} />
	}

	if (message?.messageType === 'imageMessage' && hasMediaPayload(message)) {
		const caption = getMediaCaption(message)

		return (
			<div className="scout-media-block">
				<img
					className="scout-media-image"
					src={getMediaSource(message)}
					alt={caption || 'Imagem enviada'}
					onClick={() => openMediaInNewTab(message)}
				/>
				{caption ? <p><LinkifiedText text={caption} /></p> : null}
				{isDeletedMessage ? deletedMessageLabel : null}
				{renderHistoryActionButton()}
			</div>
		)
	}

	if (message?.messageType === 'videoMessage' && hasMediaPayload(message)) {
		const caption = getMediaCaption(message)
		const isGif = Boolean(message?.gifPlayback) || getMediaMimeType(message) === 'image/gif'

		return (
			<div className="scout-media-block">
				<video className="scout-media-video" controls={!isGif} autoPlay={isGif} loop={isGif} muted={isGif} playsInline preload="metadata">
					<source src={getMediaSource(message)} type={getMediaMimeType(message)} />
				</video>
				{caption ? <p><LinkifiedText text={caption} /></p> : null}
				{isDeletedMessage ? deletedMessageLabel : null}
				{renderHistoryActionButton()}
			</div>
		)
	}

	if (message?.messageType === 'audioMessage' && hasMediaPayload(message)) {
		return (
			<div className="scout-media-block">
				<audio className="scout-media-audio" controls preload="metadata">
					<source src={getMediaSource(message)} type={getMediaMimeType(message)} />
				</audio>
				{isDeletedMessage ? deletedMessageLabel : null}
				{renderHistoryActionButton()}
			</div>
		)
	}

	if ((message?.messageType === 'documentMessage' || message?.messageType === 'documentWithCaptionMessage') && hasMediaPayload(message)) {
		return (
			<div className="scout-media-block">
				<DocumentMessageCard message={message} />
				{isDeletedMessage ? deletedMessageLabel : null}
				{renderHistoryActionButton()}
			</div>
		)
	}

	if (message?.messageType === 'contactMessage' || message?.simplified_type === 'contact') {
		return <ContactMessageCard message={message} />
	}

	if (message?.messageType === 'locationMessage' || message?.simplified_type === 'location') {
		return <LocationMessageCard message={message} />
	}

	if (message?.messageType === 'eventMessage' || message?.simplified_type === 'event') {
		return <EventMessageCard message={message} />
	}

	if (message?.messageType === 'pollCreationMessageV3' || message?.simplified_type === 'poll_creaction_message_v3') {
		return <PollMessageCard message={message} messages={messages} />
	}

	if (message?.messageType === 'stickerMessage' && hasMediaPayload(message)) {
		return (
			<div className="scout-media-block">
				<img
					className="scout-media-sticker"
					src={getMediaSource(message)}
					alt="Figurinha enviada"
					onClick={() => openMediaInNewTab(message)}
				/>
				{isDeletedMessage ? deletedMessageLabel : null}
				{renderHistoryActionButton()}
			</div>
		)
	}

	const messageText = isDeletedMessage ? 'Mensagem apagada' : formatKwikMessageContent(latestMessage)

	if (searchValue && String(searchValue).trim()) {
		return (
			<>
				<p>{highlightQuery(messageText, searchValue)}</p>
				{renderHistoryActionButton()}
			</>
		)
	}

	return (
		<>
			<p><LinkifiedText text={messageText} /></p>
			{renderHistoryActionButton()}
		</>
	)
}

export default ConversationMessageBody